import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
	Button,
	Chip,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Typography,
	makeStyles,
} from "@material-ui/core";
import { PhoneAndroid } from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
	root: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(2),
		backgroundColor: theme.palette.background.default,
		borderRadius: theme.shape.borderRadius,
		border: `1px solid ${theme.palette.divider}`,
	},
	row: {
		display: "flex",
		gap: theme.spacing(1),
		flexWrap: "wrap",
		alignItems: "center",
		marginTop: theme.spacing(1),
	},
	select: {
		minWidth: 200,
	},
}));

const BridgeDevicePairing = ({
	whatsAppId,
	compact = false,
	onPaired,
	initialDeviceSerial = "",
}) => {
	const classes = useStyles();
	const [bridgeStatus, setBridgeStatus] = useState(null);
	const [bridgeEnabled, setBridgeEnabled] = useState(false);
	const [selectedSerial, setSelectedSerial] = useState("");
	const [pairedSerial, setPairedSerial] = useState(initialDeviceSerial);

	useEffect(() => {
		setPairedSerial(initialDeviceSerial || "");
	}, [initialDeviceSerial]);

	useEffect(() => {
		let active = true;

		const loadBridgeStatus = async () => {
			try {
				const { data } = await api.get("/android-bridge/status");
				if (!active) return;
				setBridgeStatus(data);
				setBridgeEnabled(true);
			} catch (err) {
				if (active) {
					setBridgeEnabled(false);
					setBridgeStatus(null);
				}
			}
		};

		loadBridgeStatus();
		const timer = setInterval(loadBridgeStatus, 10000);
		return () => {
			active = false;
			clearInterval(timer);
		};
	}, []);

	const devices = bridgeStatus?.devices || [];

	useEffect(() => {
		if (!selectedSerial && devices.length > 0) {
			setSelectedSerial(devices[0].serial);
		}
	}, [devices, selectedSerial]);

	if (!bridgeEnabled) {
		return null;
	}

	if (pairedSerial) {
		const label =
			devices.find(device => device.serial === pairedSerial)?.label ||
			pairedSerial;

		return (
			<div className={classes.root}>
				<Chip
					size="small"
					color="primary"
					icon={<PhoneAndroid />}
					label={`${i18n.t("connections.bridge.alreadyPaired")} ${label}`}
				/>
			</div>
		);
	}

	const selectedDevice =
		devices.find(device => device.serial === selectedSerial) || devices[0];

	const handlePair = async () => {
		if (!whatsAppId || !selectedDevice?.serial) {
			return;
		}

		try {
			await api.put(`/android-bridge/pair/${whatsAppId}`, {
				deviceSerial: selectedDevice.serial,
				deviceLabel: selectedDevice.label,
			});
			setPairedSerial(selectedDevice.serial);
			toast.success(i18n.t("connections.bridge.paired"));
			onPaired?.(selectedDevice);
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			{!compact && (
				<>
					<Typography variant="subtitle2" color="primary">
						{i18n.t("connections.bridge.pairSectionTitle")}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{i18n.t("connections.bridge.pairHint")}
					</Typography>
				</>
			)}
			<div className={classes.row}>
				<Chip
					size="small"
					color={bridgeStatus?.agentOnline ? "primary" : "default"}
					label={
						bridgeStatus?.agentOnline
							? i18n.t("connections.bridge.agentOnline")
							: i18n.t("connections.bridge.agentOffline")
					}
				/>
				{devices.length === 0 ? (
					<Typography variant="body2" color="textSecondary">
						{i18n.t("connections.bridge.noDevices")}
					</Typography>
				) : (
					<>
						<FormControl
							variant="outlined"
							size="small"
							className={classes.select}
						>
							<InputLabel id={`bridge-device-${whatsAppId}`}>
								{i18n.t("connections.bridge.selectDevice")}
							</InputLabel>
							<Select
								labelId={`bridge-device-${whatsAppId}`}
								value={selectedSerial || devices[0]?.serial || ""}
								onChange={event => setSelectedSerial(event.target.value)}
								label={i18n.t("connections.bridge.selectDevice")}
							>
								{devices.map(device => (
									<MenuItem key={device.serial} value={device.serial}>
										{device.label || device.serial}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Button
							size="small"
							variant="contained"
							color="primary"
							disabled={!bridgeStatus?.agentOnline || !selectedDevice}
							onClick={handlePair}
						>
							{i18n.t("connections.bridge.pairDevice")}
						</Button>
					</>
				)}
			</div>
			{!bridgeStatus?.agentOnline && (
				<Typography variant="caption" color="textSecondary" display="block">
					{i18n.t("connections.bridge.agentRequired")}
				</Typography>
			)}
		</div>
	);
};

export default BridgeDevicePairing;
